import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Chip,
} from '@mui/material';
import { Security, Smartphone, Logout } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { supabase } from '../../supabaseClient';
import { translateAuthError } from '../../utils/authErrors';
import { useTheme } from '../../ThemeContext';
import AuthCardShell from './AuthCardShell';

// Kop van het verificatiescherm: icoon, titel en uitleg
function VerificationHeader({ theme }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
      <motion.div
        whileHover={{ scale: 1.05, rotate: 2 }}
        transition={{ type: 'spring', stiffness: 300 }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            mb: 2,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 8px 20px ${theme.palette.primary.main}40`,
          }}
        >
          <Security sx={{ fontSize: 32, color: 'white' }} />
        </Box>
      </motion.div>
      <Typography
        variant="h5"
        component="h1"
        sx={{
          fontWeight: 'bold',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          mb: 0.5,
        }}
      >
        Twee-Factor Verificatie
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mt: 0.5,
          fontWeight: 500,
          textAlign: 'center',
        }}
      >
        Voer de code uit je authenticator app in
      </Typography>
    </Box>
  );
}

// Keuze uit meerdere geverifieerde factoren (alleen getoond bij meer dan één)
function FactorChooser({ factors, selectedFactor, onSelect }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Kies authenticatie methode:
      </Typography>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          flexWrap: 'wrap',
        }}
      >
        {factors.map((factor) => (
          <Chip
            key={factor.id}
            icon={<Smartphone />}
            label={factor.friendly_name || 'Authenticator App'}
            onClick={() => onSelect(factor)}
            color={selectedFactor?.id === factor.id ? 'primary' : 'default'}
            variant={selectedFactor?.id === factor.id ? 'filled' : 'outlined'}
            clickable
          />
        ))}
      </Stack>
    </Box>
  );
}

// Codeveld met Uitloggen- en Verifiëren-knop
function VerificationForm({ theme, verifyCode, setVerifyCode, loading, onVerify, onBack }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Box component="form" onSubmit={onVerify} sx={{ mt: 1 }}>
        <TextField
          margin="normal"
          required
          fullWidth
          label="Verificatie Code"
          value={verifyCode}
          onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="123456"
          // eslint-disable-next-line jsx-a11y/no-autofocus -- bewust: gebruiker moet de verificatiecode direct kunnen typen
          autoFocus
          helperText="6-cijferige code uit je authenticator app"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
              },
              '&.Mui-focused': {
                boxShadow: `0 0 0 2px ${theme.palette.primary.main}40`,
              },
            },
          }}
          slotProps={{
            htmlInput: {
              maxLength: 6,
              inputMode: 'numeric',
              autoComplete: 'one-time-code',
              style: {
                textAlign: 'center',
                fontSize: '1.5rem',
                letterSpacing: '0.5em',
                fontWeight: 'bold',
              },
            },
          }}
        />

        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            onClick={onBack}
            startIcon={<Logout />}
            sx={{
              flex: 1,
              py: 1.5,
              borderRadius: 3,
            }}
          >
            Uitloggen
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={loading || verifyCode.length !== 6}
            sx={{
              flex: 2,
              py: 1.5,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
              fontSize: '1.1rem',
              fontWeight: 600,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                boxShadow: `0 6px 20px ${theme.palette.primary.main}60`,
              },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Verifiëren'}
          </Button>
        </Stack>
      </Box>
    </motion.div>
  );
}

const TwoFactorVerification = ({ onSuccess, onBack }) => {
  const [verifyCode, setVerifyCode] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [factors, setFactors] = useState([]);
  const [selectedFactor, setSelectedFactor] = useState(null);
  const { theme } = useTheme();

  useEffect(() => {
    loadFactors();
  }, []);

  const loadFactors = async () => {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;

      const allFactors = [...data.totp, ...data.phone];
      const verifiedFactors = allFactors.filter((factor) => factor.status === 'verified');

      setFactors(verifiedFactors);

      // Selecteer automatisch eerste factor als er maar één is
      if (verifiedFactors.length === 1) {
        setSelectedFactor(verifiedFactors[0]);
      }
    } catch (error) {
      console.error('Error loading factors:', error);
      setError(`Fout bij laden van authenticatie factoren: ${error.message}`);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();

    if (!selectedFactor) {
      setError('Geen authenticatie factor geselecteerd');
      return;
    }

    if (!verifyCode || verifyCode.length !== 6) {
      setError('Voer een geldige 6-cijferige code in');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Maak een challenge voor de geselecteerde factor
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: selectedFactor.id,
      });

      if (challengeError) throw challengeError;

      // Verificeer de code
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: selectedFactor.id,
        challengeId: challengeData.id,
        code: verifyCode,
      });

      if (verifyError) throw verifyError;

      // Succes! Roep callback aan
      onSuccess();
    } catch (error) {
      console.error('2FA verification error:', error);
      setError(translateAuthError(error, 'Verificatie mislukt. Probeer het opnieuw.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCardShell
      theme={theme}
      accentGradient={`linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`}
    >
      <VerificationHeader theme={theme} />

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
            }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        </motion.div>
      )}

      {factors.length > 1 && (
        <FactorChooser
          factors={factors}
          selectedFactor={selectedFactor}
          onSelect={setSelectedFactor}
        />
      )}

      <VerificationForm
        theme={theme}
        verifyCode={verifyCode}
        setVerifyCode={setVerifyCode}
        loading={loading}
        onVerify={handleVerify}
        onBack={onBack}
      />
    </AuthCardShell>
  );
};

export default TwoFactorVerification;
